import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { buscarPorEnfermedad } from "../../services/api";
import ProfileCard from "../../components/ProfileCard";
import { loadEnfermedades } from "../../data/enfermedades";
import toast from "react-hot-toast";
import countries from "../../data/countries";


const SearchByEnfermedad = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split("-")[0] || "en";

  const [enfermedad, setEnfermedad] = useState("");
  const [orphacode, setOrphacode] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listaEnfermedades, setListaEnfermedades] = useState([]);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const autocompleteRef = useRef(null);

  const [filtroPais, setFiltroPais] = useState("");
  const [filtroCiudad, setFiltroCiudad] = useState("");

  const [paisesDisponibles, setPaisesDisponibles] = useState([]);
  const [ciudadesDisponibles, setCiudadesDisponibles] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

    const getCountryLabel = (code) => {
      const match = countries.find((c) => c.countryCode === code);
      return match ? match.countryLabel : code;
    };

  useEffect(() => {
    loadEnfermedades(currentLang).then(setListaEnfermedades);
  }, [currentLang]);

  useEffect(() => {
    const paises = Array.from(new Set(results.map((p) => p.country).filter(Boolean)));
    setPaisesDisponibles(paises);
    setFiltroPais("");
    setFiltroCiudad("");
  }, [results]);

  useEffect(() => {
    if (filtroPais) {
      const ciudades = Array.from(new Set(
        results.filter((p) => p.country === filtroPais).map((p) => p.ciudad).filter(Boolean)
      ));
      setCiudadesDisponibles(ciudades);
    } else {
      setCiudadesDisponibles([]);
    }
    setFiltroCiudad("");
  }, [filtroPais]);

  const handleSearch = async () => {
    if (!orphacode) {
      toast.error(t("search_disease.empty_error", "❗ Por favor, selecciona una enfermedad válida."));
      return;
    }

    setLoading(true);
    setHasSearched(true); // 👉 Aquí marcamos que se ha buscado
    try {
      const res = await buscarPorEnfermedad(orphacode);
      setResults(res.data);
    } catch (err) {
      console.error("Error al buscar perfiles:", err);
      toast.error(t("search_disease.search_error", "❌ Error al buscar perfiles"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setEnfermedad(val);

    const match = listaEnfermedades.find((e) => e.name === val);
    setOrphacode(match ? match.code : null);
    setMostrarSugerencias(Boolean(val.trim()) && !match);
  };

  const seleccionarEnfermedad = (item) => {
    setEnfermedad(item.name);
    setOrphacode(item.code);
    setMostrarSugerencias(false);
  };

  useEffect(() => {
    const cerrarAlPulsarFuera = (event) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target)
      ) {
        setMostrarSugerencias(false);
      }
    };

    document.addEventListener("pointerdown", cerrarAlPulsarFuera);

    return () => {
      document.removeEventListener("pointerdown", cerrarAlPulsarFuera);
    };
  }, []);

  const textoBusqueda = enfermedad.trim().toLocaleLowerCase();

  const sugerencias = textoBusqueda
    ? listaEnfermedades
        .filter((item) =>
          item.name.toLocaleLowerCase().includes(textoBusqueda)
        )
        .slice(0, 30)
    : [];

  const filteredResults = results.filter((p) => {
    return (
      (!filtroPais || p.country === filtroPais) &&
      (!filtroCiudad || p.ciudad === filtroCiudad)
    );
  });

  return (
    <div className="max-w-xl mx-auto mt-2 md:mt-[40px] px-4 text-center">
        <h2
          className="text-2xl font-semibold mb-4 text-[#34568B] drop-shadow-md animate-fade-in"
          style={{ animation: "fadeInUp 1s ease-out" }}
        >
          {t("search_disease.title", "Búsqueda de usuarios por enfermedad")}
        </h2>

      <div ref={autocompleteRef} className="relative mb-4">
        <input
          type="text"
          value={enfermedad}
          onChange={handleChange}
          onFocus={() => {
            if (enfermedad.trim() && !orphacode) {
              setMostrarSugerencias(true);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setMostrarSugerencias(false);
            }
          }}
          autoComplete="off"
          placeholder={t("search_disease.placeholder", "Escribe una enfermedad")}
          aria-label={t("search_disease.placeholder", "Escribe una enfermedad")}
          aria-expanded={mostrarSugerencias && sugerencias.length > 0}
          aria-controls="enfermedades-sugerencias"
          className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8]/20"
        />

        {mostrarSugerencias && sugerencias.length > 0 && (
          <ul
            id="enfermedades-sugerencias"
            role="listbox"
            className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[260px] overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-white py-1 text-left shadow-xl sm:left-1/2 sm:right-auto sm:w-[min(800px,calc(100vw-32px))] sm:max-h-[360px] sm:-translate-x-1/2"
            style={{ backgroundColor: "#ffffff" }}
          >
            {sugerencias.map((item) => (
              <li key={item.code} role="option" aria-selected={false}>
                <button
                  type="button"
                  onClick={() => seleccionarEnfermedad(item)}
                  className="block w-full whitespace-normal break-words border-b border-slate-100 bg-white px-4 py-3 text-left text-base leading-6 text-slate-800 transition hover:bg-[#EFF7FC] focus:bg-[#EFF7FC] focus:outline-none last:border-b-0 sm:px-5 sm:py-3.5"
                >
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <button
        onClick={handleSearch}
        disabled={!orphacode || loading}
        className="rounded-full bg-[#16A34A] px-7 py-2.5 text-[15px] font-semibold text-white shadow-sm transition hover:bg-[#15803D] hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? t("search_disease.loading", "Buscando…") : t("search_disease.button", "Buscar")}
      </button>

      {filteredResults.length > 0 && (
        <div className="mt-8 text-left">

          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:gap-4">
            <select
              value={filtroPais}
              onChange={(e) => setFiltroPais(e.target.value)}
              className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-base sm:text-[15px] text-slate-700 shadow-sm outline-none transition hover:border-[#B8D8EA] focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8]/15"
            >
              <option value="">{t("filters.country", "Filtrar por país")}</option>
                {paisesDisponibles.map((pais) => (
                  <option key={pais} value={pais}>
                    {getCountryLabel(pais)}
                  </option>
                ))}
            </select>

            {ciudadesDisponibles.length > 0 && (
              <select
                value={filtroCiudad}
                onChange={(e) => setFiltroCiudad(e.target.value)}
                className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-base sm:text-[15px] text-slate-700 shadow-sm outline-none transition hover:border-[#B8D8EA] focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8]/15"
              >
                <option value="">{t("filters.city", "Filtrar por ciudad")}</option>
                {ciudadesDisponibles.map((ciudad) => (
                  <option key={ciudad} value={ciudad}>{ciudad}</option>
                ))}
              </select>
            )}
          </div>

          <div className="grid gap-4 sm:gap-5 [&_article]:sm:p-6 [&_article_h3]:sm:text-lg [&_article_.text-sm]:sm:text-[15px] [&_article_.leading-5]:sm:leading-6">
            {filteredResults.map((profile) => (
              <ProfileCard
                key={profile.id}
                profile={profile}
                onFollowToggle={() => {}}
              />
            ))}
          </div>
        </div>
      )}
            {hasSearched && !loading && results.length === 0 && (
              <p className="text-center text-gray-500 mt-6">
                {t("search_disease.no_results", "No hay todavía perfiles con tu misma enfermedad.")}
              </p>
            )}

            {hasSearched && !loading && results.length > 0 && filteredResults.length === 0 && (
              <p className="text-center text-gray-500 mt-6">
                {t("search_disease.no_filtered_results", "No hay todavía perfiles con tu misma enfermedad en esa ubicación.")}
              </p>
            )}
    </div>
  );
};

export default SearchByEnfermedad;
